import { Tooltip } from '@/components/ui/Tooltip';
import { cn } from '@/utils/cn';
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';

type TableRowActionsLayout = 'inline' | 'wrap' | 'stack';

interface TableRowActionsProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** inline: single row (default). wrap: allow wrapping for many actions. stack: vertical (e.g. input + buttons). */
  layout?: TableRowActionsLayout;
}

const layoutClass: Record<TableRowActionsLayout, string> = {
  inline: 'table-row-actions table-row-actions--inline',
  wrap: 'table-row-actions table-row-actions--wrap',
  stack: 'table-row-actions table-row-actions--stack',
};

/** Compact action button group for DataTable row columns. */
export function TableRowActions({
  children,
  layout = 'inline',
  className,
  ...props
}: TableRowActionsProps) {
  return (
    <div className={cn(layoutClass[layout], className)} {...props}>
      {children}
    </div>
  );
}

/** Compact text action for DataTable rows (no white button chrome). */
export function TableRowActionLink({
  className,
  children,
  title,
  tooltip,
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tooltip?: ReactNode }) {
  const hint = tooltip ?? title;
  const button = (
    <button
      type="button"
      title={typeof hint === 'string' ? hint : title}
      disabled={disabled}
      className={cn('table-row-action-link', className)}
      {...props}
    >
      {children}
    </button>
  );

  if (hint && typeof hint !== 'string') {
    return <Tooltip content={hint}>{button}</Tooltip>;
  }
  return button;
}

export function isActionsColumn(col: { key: string; header: string; actions?: boolean }): boolean {
  if (col.actions) return true;
  if (col.key === 'actions') return true;
  return col.header.trim().toLowerCase() === 'actions';
}

export const ACTIONS_COLUMN_CLASS = 'whitespace-nowrap align-top text-left';
